import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, HelpCircle, Shield, FileText, LogOut, ChevronLeft } from 'lucide-react';
import { useStore } from '@tanstack/react-store';
import { themeStore } from '../../../stores/theme';
import { useLogout } from '../../../hooks/useAuth';
import toast from 'react-hot-toast';
import Modal from '../../common/ModalComponent';

// Main App Component
const Account = () => {
    const { isDarkMode } = useStore(themeStore)
    const navigate = useNavigate()
    const logout = useLogout()
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    const colors = {
        text: isDarkMode ? '#e0e0e0' : '#333333',
        border: isDarkMode ? '#333333' : '#e0e0e0',
        inputBackground: isDarkMode ? '#2d2d2d' : '#ffffff'
    };

    const handleLogout = async () => {
        logout.mutate(null, {
            onSuccess: () => {
                navigate('/login');
                toast.success('Logged out successfully');
            },
            onError: () => {
                navigate('/login');
                toast.error('Session expired, logged out');
            }
        })
    };

    const profileOptions = [
        {
            id: 'edit-profile',
            title: 'Edit Profile',
            icon: User,
            variant: 'primary',
            onClick: () => navigate('/profile')
        },
        {
            id: 'contact-us',
            title: 'Contact Us',
            icon: Mail,
            variant: 'secondary',
            onClick: () => navigate('/contact-form')
        },
        {
            id: 'faq',
            title: 'FAQ',
            icon: HelpCircle,
            variant: 'secondary',
            onClick: () => navigate('/faqs')
        },
        {
            id: 'privacy-policy',
            title: 'Privacy Policy',
            icon: Shield,
            variant: 'secondary',
            onClick: () => navigate('/privacy')
        },
        {
            id: 'terms-conditions',
            title: 'Terms & Conditions',
            icon: FileText,
            variant: 'secondary',
            onClick: () => navigate('/terms')
        },
        {
            id: 'logout',
            title: 'Logout',
            icon: LogOut,
            variant: 'secondary',
            onClick: () => setShowLogoutModal(true)
        }
    ];

    return (
        <>
            <div className={`min-h-screen items-center justify-center p-4 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
                <div className="w-[80%] mt-6 mx-auto flex mb-6">
                    <button onClick={() => navigate(-1)} className="mr-4 p-2 border rounded-lg transition-colors">
                        <ChevronLeft className="w-6 h-6 text-gray-600" />
                    </button>
                    <p className={`w-full text-3xl poppins-regular ${isDarkMode?"text-white":"text-gray-900"} mt-1 mb-6`} style={{ textAlign: "center" }}>ACCOUNT</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[50px] border p-10 rounded-xl max-w-[80%] shadow mx-auto">
                    {profileOptions.map((option) => {
                        const IconComponent = option.icon;
                        return (
                            <button
                                key={option.id}
                                onClick={option.onClick}
                                className={`group relative w-full p-2 rounded-2xl border-2 transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95 ${isDarkMode?"bg-gray-800 hover:bg-gray-900":"bg-white hover:bg-gray-50"} border-gray-200 hover:border-gray-300 `}>
                                <div className="flex items-center space-x-4">
                                    <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors bg-blue-100 text-blue-600 group-hover:bg-blue-200`}>
                                        <IconComponent className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <h3 className={`text-lg transition-colors ${isDarkMode?"text-white group-hover:text-white":"text-gray-900 group-hover:text-gray-700"}`}>
                                            {option.title}
                                        </h3>
                                    </div>
                                    {/* Subtle arrow indicator */}
                                    <div className={`flex-shrink-0 transition-transform group-hover:translate-x-1 text-gray-400`}>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                                {/* Hover effect overlay */}
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent to-blue-50 opacity-0 group-hover:opacity-30 transition-opacity pointer-events-none" />
                            </button>
                        );
                    })}
                </div>
            </div>
            {/* Confirm Logout Modal */}
            <Modal isDarkMode={isDarkMode} key={"confirmlogout"} title="Confirm Logout" isOpen={showLogoutModal} onClose={() => setShowLogoutModal(false)}>
                <div className="mb-6">
                    <p className={`${isDarkMode? "text-white": "text-gray-800"}`}> Are you sure, you want to logout from this account ?</p>
                    <div className='text-center mt-5'>
                        <button
                            onClick={() => setShowLogoutModal(false)}
                            className={`w-[200px] py-3 text-center font-medium ${isDarkMode? "hover:bg-gray-700":"hover:bg-gray-100"} transition rounded-full mr-3`}
                            style={{ color: colors.text }}>
                            Cancel
                        </button>
                        <button
                            onClick={handleLogout}
                            className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mb-3">
                            Confirm
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
export default Account